import * as moment from 'moment';

/**
 * Change-detection tasks don't repeat on arbitrary days: the backend runs them on
 * a fixed Monday schedule. The number of repeats (and therefore the cost) is the
 * number of those scheduled Monday runs that fall inside the task's TOI, rather than
 * the generic "TOI length / frequency" calculation used for other recurring tasks.
 */
export type ChangeDetectionFrequency = 'Weekly' | 'Fortnightly' | 'Monthly';

const MONDAY_RUN_HOUR_UTC = 4; // 09:30 in Asia/Kolkata
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** Normalizes a TOI boundary to epoch milliseconds (UTC). */
function toUtcInstant(value: string | Date | moment.Moment): number {
  if (typeof value === 'string') {
    // An ISO window already carries its UTC offset.
    return moment.parseZone(value).valueOf();
  }
  // Task windows are sent to newtask with this same wall-time-to-UTC conversion,
  // i.e. the local wall-clock time is kept and simply relabelled as UTC.
  return moment(value).utcOffset(0, true).valueOf();
}

/**
 * Epoch ms of the run time on the given UTC calendar day.
 * Date.UTC rolls overflowing month/day values over, so callers may pass
 * e.g. month 12 or day 35 and get the correct date in the next month/year.
 */
function mondayAtRunTime(year: number, month: number, date: number): number {
  return Date.UTC(year, month, date, MONDAY_RUN_HOUR_UTC);
}

/** First Monday run instant that is >= `start`. */
function firstMondayOnOrAfter(start: number): number {
  const date = new Date(start);
  // getUTCDay(): Sun=0 .. Sat=6, so this gives 0 on a Monday and 1..6 otherwise.
  const daysUntilMonday = (8 - date.getUTCDay()) % 7;
  let monday = mondayAtRunTime(
    date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + daysUntilMonday
  );
  // If start is a Monday but after the run hour, today's run has already been
  // missed, so the first eligible run is next Monday.
  if (monday < start) {
    monday += 7 * ONE_DAY_MS;
  }
  return monday;
}

/** Run instant on the first Monday of the given month (month may overflow to next year). */
function firstMondayOfMonth(year: number, month: number): number {
  const first = new Date(Date.UTC(year, month, 1));
  return mondayAtRunTime(year, month, 1 + (8 - first.getUTCDay()) % 7);
}

/**
 * Scheduled 09:30 IST Monday runs whose instants are inside the TOI, inclusive.
 *
 * - Weekly:      every Monday from the first eligible Monday.
 * - Fortnightly: every second Monday from the first eligible Monday.
 * - Monthly:     the first eligible Monday, then the first Monday of each
 *                following month (not "+1 month", which would drift off Monday).
 *
 * Returns an empty list for invalid dates or an inverted window.
 */
export function getChangeDetectionRunDates(
  openWindow: string | Date | moment.Moment,
  closeWindow: string | Date | moment.Moment,
  frequency: ChangeDetectionFrequency
): Date[] {
  const start = toUtcInstant(openWindow);
  const end = toUtcInstant(closeWindow);
  if (!Number.isFinite(start) || !Number.isFinite(end) || start > end) {
    return [];
  }

  const runs: Date[] = [];
  let run = firstMondayOnOrAfter(start);
  // `<=` so a run landing exactly on the close window still counts.
  while (run <= end) {
    runs.push(new Date(run));
    if (frequency === 'Monthly') {
      const date = new Date(run);
      run = firstMondayOfMonth(date.getUTCFullYear(), date.getUTCMonth() + 1);
    } else {
      // Fixed-ms steps are safe here: everything is in UTC, so there is no DST shift.
      run += (frequency === 'Fortnightly' ? 14 : 7) * ONE_DAY_MS;
    }
  }
  return runs;
}

/**
 * Number of change-detection runs inside the TOI; used as `no_of_repeats` for
 * change-detection tasks. Can be 0 when no scheduled Monday run falls in the TOI.
 */
export function countChangeDetectionRepeats(
  openWindow: string | Date | moment.Moment,
  closeWindow: string | Date | moment.Moment,
  frequency: ChangeDetectionFrequency
): number {
  return getChangeDetectionRunDates(openWindow, closeWindow, frequency).length;
}
